import { Component, OnInit, ChangeDetectorRef, ViewEncapsulation, OnDestroy, AfterViewInit } from '@angular/core';
import { RequestService } from 'src/app/services/request.service';
import { ChartOptions, ChartData } from 'chart.js';
import { MatDialog } from '@angular/material/dialog';
import { NutsInfoDialogComponent } from './nuts-info-dialog/nuts-info-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import Swal from 'sweetalert2';
declare var google: any;

@Component({
  selector: 'app-regional-outputs',
  templateUrl: './regional-outputs.component.html',
  styleUrls: ['./regional-outputs.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RegionalOutputsComponent implements OnInit, OnDestroy, AfterViewInit {

  map2: google.maps.Map | null = null;
  radarAvailable: boolean = false;
  loading: boolean = false;
  radarChartOptions: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
  };
  radarChartLabels = ['Hydraulic Conductivity', 'Soil Organic Carbon', 'Yield', 'Test'];
  radarData = [{ label: '', data: [0, 0, 0, 0] }];
  debounceTimer: any;
  noData: boolean = false;
  currentOrganicPerc: any | null = null;
  targetOrganicPerc: any | null = null;
  nutsId: any = '';

  slidersVisible: boolean = false;
  selectedNcp: any = '';
  ncpLabels: string[] = ["soil organic carbon", "yield", "hydraulic conductivity"];
  lineChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Proportion of arable land under organic farming system'
        }
      }
    }
  };

  lineChartData: ChartData<'line'> = {
    labels: [],
    datasets: []
  };
  lineChartAvailable: boolean = false;
  responseData: any = '';

  densityChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
  };

  densityChartData: ChartData<'line'> = {
    labels: [],
    datasets: []
  };

  tableData = new MatTableDataSource<any>();
  displayedColumns: string[] = ['ncp', 'current', 'target', 'effect', 'unit'];

  selectedChart: 'line' | 'density' | 'table' = 'line';
  lat: number = 0;
  lon: number = 0;

  // Enhanced state management
  private chartDataCache: {
    line: ChartData<'line'> | null;
    density: ChartData<'line'> | null;
    table: MatTableDataSource<any> | null;
  } = {
    line: null,
    density: null,
    table: null
  };

  private isDataLoaded: boolean = false;
  private currentSelectedNcp: string = '';

  constructor(private req: RequestService<any>, private cdr: ChangeDetectorRef, public dialog: MatDialog) { }

  ngOnInit(): void {
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 0);
    this.initMap();
  }

  ngOnDestroy(): void {
    if (this.map2) {
      this.map2 = null;
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  ngAfterViewInit(): void {
    if (this.map2) {
      google.maps.event.trigger(this.map2, 'resize');
    }
  }

  initMap(): void {
    const mapContainer2 = document.getElementById('map2');
    if (this.map2 && mapContainer2) {
      mapContainer2.innerHTML = '';
    }

    const europeBounds = {
      north: 71.0,
      south: 33.0,
      west: -24.0,
      east: 50.0,
    };

    this.map2 = new google.maps.Map(mapContainer2!, {
      zoom: 2,
      center: { lat: 45.0, lng: 14.0 },
      mapTypeId: google.maps.MapTypeId.hybrid,
      mapTypeControl: false,
      streetViewControl: false,
    });

    this.map2!.setOptions({
      restriction: {
        latLngBounds: europeBounds,
        strictBounds: true,
      },
    });

    this.req.getGeoData().subscribe((res: any) => {
      const infoWindow = new google.maps.InfoWindow();

      res.forEach((feature: any) => {
        const coordinates = feature.geometry.coordinates;
        const isMultiPolygon = feature.geometry.type === "MultiPolygon";
        const paths = isMultiPolygon
          ? coordinates.flatMap((polygon: any) =>
            polygon.map((ring: any) =>
              ring.map((coord: [number, number]) => ({
                lat: coord[1],
                lng: coord[0],
              }))
            )
          )
          : coordinates.map((ring: any) =>
            ring.map((coord: [number, number]) => ({
              lat: coord[1],
              lng: coord[0],
            }))
          );

        paths.forEach((polygonPath: any) => {
          const mapPolygon = new google.maps.Polygon({
            paths: polygonPath,
            strokeColor: "#000000",
            strokeOpacity: 0.4,
            strokeWeight: 1,
            fillColor: "#757575",
            fillOpacity: 0.05,
          });

          mapPolygon.setMap(this.map2);

          mapPolygon.addListener('mouseover', (event: any) => {
            const content = `
              <div style="font-size: 14px; line-height: 2;">
                <strong>${feature.properties.NUTS_ID || 'N/A'}</strong> <br>
                ${feature.properties.NAME_LATN || 'N/A'}
              </div>
            `;

            infoWindow.setContent(content);
            infoWindow.setPosition(event.latLng);

            google.maps.event.addListenerOnce(infoWindow, 'domready', () => {
              const closeButton = document.querySelector('.gm-ui-hover-effect') as HTMLElement;
              if (closeButton) {
                closeButton.style.display = 'none';
              }
            });

            infoWindow.open(this.map2);
          });

          mapPolygon.addListener('mouseout', () => {
            infoWindow.close();
          });

          mapPolygon.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (this.debounceTimer) clearTimeout(this.debounceTimer);
            this.lat = event.latLng.lat();
            this.lon = event.latLng.lng();

            this.debounceTimer = setTimeout(() => {
              this.nutsId = feature.properties.NUTS_ID;
              this.slidersVisible = true;
              this.cdr.detectChanges();
            }, 100);
          });

        });
      });
    })
  }

  formatLabel(value: number): string {
    return Math.round(value) + '%';
  }

  get canLoadRadarData(): boolean {
    return this.currentOrganicPerc > 0 && this.targetOrganicPerc > 0;
  }

  loadRadarData(): void {
    Swal.fire({
      title: "This action requires up to 60 seconds to complete.",
      text: "Are you sure you want to continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#5da36e",
      cancelButtonColor: "#d33",
      confirmButtonText: "Continue",
    }).then((result) => {
      if (result.isConfirmed) {
        this.loading = true;
        this.resetAllData();
        
        this.req.getRegionalOutputs(this.nutsId, this.currentOrganicPerc, this.targetOrganicPerc, this.lat, this.lon)
          .subscribe({
            next: (res: any) => {
              this.loading = false;
              if (res && res.length > 0) {
                this.responseData = res;
                this.processRadarData(res);
                this.radarAvailable = true;
                this.isDataLoaded = true;
                
                // Set default NCP if not already selected or if it doesn't exist in new data
                if (!this.selectedNcp || !this.isValidNcp(this.selectedNcp)) {
                  this.selectedNcp = this.ncpLabels[0] || '';
                }
                
                // Initialize chart data cache
                this.initializeChartDataCache();
                
              } else {
                this.noData = true;
                this.radarAvailable = false;
                this.isDataLoaded = false;
              }
              this.cdr.detectChanges();
            },
            error: (error) => {
              console.error('Error loading radar data:', error);
              this.loading = false;
              this.noData = true;
              this.radarAvailable = false;
              this.isDataLoaded = false;
              this.cdr.detectChanges();
            }
          });
      }
    });
  }

private processRadarData(res: any): void {
  // Extract the standardized values from the new response structure
  const standardizedCurrent = res[0].standardized_values_current;
  const standardizedTarget = res[0].standardized_values_target;
  
  if (!standardizedCurrent || !standardizedTarget) {
    console.error('Missing standardized values in response');
    return;
  }
  
  // Get the keys (parameter names) and values
  const parameterNames = Object.keys(standardizedCurrent);
  const currentValues = parameterNames.map(key => parseFloat(standardizedCurrent[key]));
  const targetValues = parameterNames.map(key => parseFloat(standardizedTarget[key]));
  
  // Set up the radar chart labels (just the parameter names)
  this.radarChartLabels = parameterNames;
  
  // Create the radar chart datasets with red and blue colors
  this.radarData = [
    {
      label: `Current`,
      data: currentValues,
      borderColor: 'rgb(239, 68, 68)', // Red color
      backgroundColor: 'rgba(239, 68, 68, 0.2)',
      pointBorderColor: 'rgb(239, 68, 68)',
      pointBackgroundColor: 'rgba(239, 68, 68, 0.8)',
      borderWidth: 2,
      pointRadius: 6
    } as any,
    {
      label: `Target`,
      data: targetValues,
      borderColor: 'rgb(59, 130, 246)', // Blue color
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      pointBorderColor: 'rgb(59, 130, 246)',
      pointBackgroundColor: 'rgba(59, 130, 246, 0.8)',
      borderWidth: 2,
      pointRadius: 6
    } as any
  ];
  
  // Update radar chart options to show numbers on radial lines (X-axis equivalent)
  this.radarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 1,
    scales: {
      r: {
        beginAtZero: true,
        min: 0,
        max: 100,
        ticks: {
          display: true,
          stepSize: 25, // This creates ticks at 0, 25, 50, 75, 100
          font: {
            size: 12,
            weight: 'bold'
          },
          color: '#374151', // Gray color for tick labels
          backdropColor: 'rgba(255, 255, 255, 0.8)', // White background for better readability
          backdropPadding: 4
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.2)', // Make grid lines more visible
          lineWidth: 1
        },
        angleLines: {
          color: 'rgba(0, 0, 0, 0.2)', // Make angle lines more visible
          lineWidth: 1
        },
        pointLabels: {
          font: {
            size: 14,
            weight: 'bold'
          },
          color: '#1f2937' // Dark gray for parameter labels
        }
      }
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 20,
          font: {
            size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            return `${context.dataset.label}: ${context.parsed.r.toFixed(2)}`;
          }
        }
      }
    }
  };
  }

  private initializeChartDataCache(): void {
    if (!this.isDataLoaded || !this.selectedNcp) {
      return;
    }

    // Initialize all chart types at once to avoid repeated calculations
    this.chartDataCache.line = this.generateLineChartData(this.selectedNcp);
    this.chartDataCache.density = this.generateDensityChartData(this.selectedNcp);
    this.chartDataCache.table = this.generateTableData();
    
    this.currentSelectedNcp = this.selectedNcp;
    this.updateActiveChart();
  }

  private isValidNcp(ncp: string): boolean {
    return this.ncpLabels.some(label => label.toLowerCase() === ncp.toLowerCase());
  }

  onNcpChange(selected: string): void {
    console.log('NCP Change triggered:', selected, 'Current:', this.currentSelectedNcp, 'Data loaded:', this.isDataLoaded);
    
    if (!this.isDataLoaded || !selected) {
      console.log('Early return - no data loaded or no selection');
      return;
    }

    // Always update selectedNcp first
    this.selectedNcp = selected;
    this.currentSelectedNcp = selected;

    try {
      // Regenerate chart data for the new NCP with better error handling
      const lineData = this.generateLineChartData(selected);
      const densityData = this.generateDensityChartData(selected);
      const tableData = this.generateTableData();

      // Only update cache if generation was successful
      if (lineData) this.chartDataCache.line = lineData;
      if (densityData) this.chartDataCache.density = densityData;
      if (tableData) this.chartDataCache.table = tableData;

      console.log('Chart data generated:', {
        line: !!lineData,
        density: !!densityData,
        table: !!tableData,
        selectedChart: this.selectedChart
      });

      // Force update the currently active chart
      this.forceUpdateActiveChart();
      
    } catch (error) {
      console.error('Error generating chart data for NCP:', selected, error);
      // Don't clear existing data on error, just log it
    }
  }

  private updateActiveChart(): void {
    console.log('Updating active chart:', this.selectedChart, 'Available data:', {
      line: !!this.chartDataCache.line,
      density: !!this.chartDataCache.density,
      table: !!this.chartDataCache.table
    });

    switch (this.selectedChart) {
      case 'line':
        if (this.chartDataCache.line) {
          this.lineChartData = { ...this.chartDataCache.line };
          this.lineChartAvailable = true;
        } else {
          console.warn('No line chart data available');
          this.lineChartAvailable = false;
        }
        break;
      case 'density':
        if (this.chartDataCache.density) {
          this.densityChartData = { ...this.chartDataCache.density };
          this.lineChartAvailable = true;
        } else {
          console.warn('No density chart data available');
          this.lineChartAvailable = false;
        }
        break;
      case 'table':
        if (this.chartDataCache.table) {
          this.tableData = new MatTableDataSource(this.chartDataCache.table.data);
          this.lineChartAvailable = true;
        } else {
          console.warn('No table data available');
          this.lineChartAvailable = false;
        }
        break;
    }
    
    // Force change detection
    this.cdr.detectChanges();
  }

  private forceUpdateActiveChart(): void {
    // More aggressive update that ensures chart visibility
    const wasAvailable = this.lineChartAvailable;
    
    switch (this.selectedChart) {
      case 'line':
        if (this.chartDataCache.line && this.chartDataCache.line.datasets && this.chartDataCache.line.datasets.length > 0) {
          this.lineChartData = JSON.parse(JSON.stringify(this.chartDataCache.line)); // Deep copy
          this.lineChartAvailable = true;
          console.log('Line chart updated with data:', this.lineChartData);
        } else {
          console.error('Line chart data invalid or missing');
          this.lineChartAvailable = false;
        }
        break;
      case 'density':
        if (this.chartDataCache.density && this.chartDataCache.density.datasets && this.chartDataCache.density.datasets.length > 0) {
          this.densityChartData = JSON.parse(JSON.stringify(this.chartDataCache.density)); // Deep copy
          this.lineChartAvailable = true;
          console.log('Density chart updated with data:', this.densityChartData);
        } else {
          console.error('Density chart data invalid or missing');
          this.lineChartAvailable = false;
        }
        break;
      case 'table':
        if (this.chartDataCache.table && this.chartDataCache.table.data && this.chartDataCache.table.data.length > 0) {
          this.tableData = new MatTableDataSource([...this.chartDataCache.table.data]); // New instance
          this.lineChartAvailable = true;
          console.log('Table updated with data:', this.tableData.data);
        } else {
          console.error('Table data invalid or missing');
          this.lineChartAvailable = false;
        }
        break;
    }
    
    // Force multiple change detection cycles if availability changed
    if (wasAvailable !== this.lineChartAvailable) {
      this.cdr.detectChanges();
      setTimeout(() => {
        this.cdr.detectChanges();
      }, 0);
    } else {
      this.cdr.detectChanges();
    }
  }

  private generateLineChartData(selectedNcp: string): ChartData<'line'> | null {
    console.log('Generating line chart data for:', selectedNcp);
    
    if (!this.responseData || !selectedNcp) {
      console.log('No response data or selectedNcp');
      return null;
    }

    console.log('Available response data items:', this.responseData.length);
    
    const selectedData = this.responseData.find((item: any) => {
      const hasLineGraphData = item.line_graph_data && item.line_graph_data.NCP_name;
      const ncpMatches = hasLineGraphData && 
        item.line_graph_data.NCP_name.toLowerCase().trim() === selectedNcp.toLowerCase().trim();
      
      console.log('Checking item:', {
        hasLineGraphData,
        ncpName: hasLineGraphData ? item.line_graph_data.NCP_name : 'none',
        ncpMatches,
        selectedNcp
      });
      
      return ncpMatches;
    });

    if (!selectedData || !selectedData.line_graph_data) {
      console.log('No matching data found for NCP:', selectedNcp);
      console.log('Available NCPs:', this.responseData.map((item: any) => 
        item.line_graph_data ? item.line_graph_data.NCP_name : 'no line_graph_data'
      ));
      return null;
    }

    const { organic_perc, output } = selectedData.line_graph_data;
    
    if (!organic_perc || !output || organic_perc.length === 0 || output.length === 0) {
      console.log('Invalid organic_perc or output data');
      return null;
    }

    const unitIndex = this.ncpLabels.findIndex(
      (ncp: string) => ncp.toLowerCase().trim() === selectedNcp.toLowerCase().trim()
    );
    const unit = unitIndex !== -1 && this.responseData[0].table_data ? 
      this.responseData[0].table_data.Unit[unitIndex] : '';

    const chartData = {
      labels: [...organic_perc],
      datasets: [
        {
          label: `${selectedNcp} (${unit})`,
          data: [...output],
          borderColor: 'rgb(56, 150, 150)',
          backgroundColor: 'rgb(117, 192, 192)',
          pointBorderColor: 'rgb(56, 150, 150)',
          borderWidth: 2,
          pointBackgroundColor: 'rgb(117, 192, 192)',
          fill: false
        }
      ]
    };

    console.log('Generated line chart data:', chartData);
    return chartData;
  }

  private generateDensityChartData(selectedNcp: string): ChartData<'line'> | null {
    console.log('Generating density chart data for:', selectedNcp);
    
    if (!this.responseData || !selectedNcp) {
      console.log('No response data or selectedNcp for density');
      return null;
    }

    const selectedData = this.responseData.find(
      (item: any) => {
        const hasDensityData = item.density_plot_data && item.density_plot_data.hasOwnProperty(selectedNcp);
        console.log('Checking density item for', selectedNcp, ':', {
          hasDensityData,
          availableKeys: item.density_plot_data ? Object.keys(item.density_plot_data) : []
        });
        return hasDensityData;
      }
    );

    if (!selectedData || !selectedData.density_plot_data[selectedNcp]) {
      console.log('No density data found for:', selectedNcp);
      console.log('Available density keys:', this.responseData.map((item: any) => 
        item.density_plot_data ? Object.keys(item.density_plot_data) : []
      ).flat());
      return null;
    }

    const selectedNcpData = selectedData.density_plot_data[selectedNcp];
    let { x_conventional, x_organic, y_conventional, y_organic } = selectedNcpData;

    if (!x_conventional || !x_organic || !y_conventional || !y_organic ||
        x_conventional.length === 0 || x_organic.length === 0) {
      console.log('Invalid density plot arrays');
      return null;
    }

    const sharedX = Array.from(new Set([...x_conventional, ...x_organic])).sort((a, b) => a - b);

    const interpolateY = (xArray: number[], yArray: number[], xShared: number[]) => {
      return xShared.map((x) => {
        let i = xArray.findIndex((xi) => xi >= x);
        if (i === -1) return yArray[yArray.length - 1];
        if (i === 0) return yArray[0];
        let x0 = xArray[i - 1],
          x1 = xArray[i],
          y0 = yArray[i - 1],
          y1 = yArray[i];
        return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
      });
    };

    const interpolatedYConventional = interpolateY(x_conventional, y_conventional, sharedX);
    const interpolatedYOrganic = interpolateY(x_organic, y_organic, sharedX);

    const chartData = {
      labels: [...sharedX],
      datasets: [
        {
          label: 'Conventional Output',
          data: sharedX.map((x, i) => ({ x, y: interpolatedYConventional[i] })),
          borderColor: 'rgb(56, 150, 150)',
          backgroundColor: 'rgba(117, 192, 192, 0.5)',
          pointBorderColor: 'rgb(56, 150, 150)',
          borderWidth: 2,
          pointBackgroundColor: 'rgb(154, 219, 219)',
          fill: true,
          showLine: true
        },
        {
          label: 'Organic Output',
          data: sharedX.map((x, i) => ({ x, y: interpolatedYOrganic[i] })),
          borderColor: 'rgb(156, 141, 57)',
          backgroundColor: 'rgba(231, 219, 151, 0.5)',
          pointBorderColor: 'rgb(156, 141, 57)',
          borderWidth: 2,
          pointBackgroundColor: 'rgb(212, 200, 128)',
          fill: true,
          showLine: true
        }
      ]
    };

    console.log('Generated density chart data:', chartData);
    return chartData;
  }

  private generateTableData(): MatTableDataSource<any> | null {
    if (!this.responseData || !this.responseData[0] || !this.responseData[0].table_data) {
      return null;
    }

    const selectedTableData = this.responseData[0].table_data;
    const formattedData = selectedTableData.NCP.map((ncp: string, index: number) => ({
      ncp: ncp,
      unit: selectedTableData.Unit[index],
      current: `${parseFloat(selectedTableData.Current_Organic_Output[index]).toFixed(2)}`,
      target: `${parseFloat(selectedTableData.Target_Organic_Output[index]).toFixed(2)} `,
      effect: `${parseFloat(selectedTableData.Effect_of_Shift[index]).toFixed(2)}`,
    }));

    return new MatTableDataSource(formattedData);
  }

  onChartSelectionChange(): void {
    console.log('Chart selection changed to:', this.selectedChart, 'Data loaded:', this.isDataLoaded);
    
    if (!this.isDataLoaded) {
      console.log('No data loaded, skipping chart change');
      return;
    }

    // Force update the active chart with current cached data
    this.forceUpdateActiveChart();
  }

  private resetAllData(): void {
    this.noData = false;
    this.radarAvailable = false;
    this.lineChartAvailable = false;
    this.isDataLoaded = false;
    this.responseData = '';
    this.selectedNcp = '';
    this.currentSelectedNcp = '';
    
    // Reset chart data
    this.radarData = [{ label: '', data: [0, 0, 0, 0] }];
    this.lineChartData = { labels: [], datasets: [] };
    this.densityChartData = { labels: [], datasets: [] };
    this.tableData = new MatTableDataSource<any>();
    
    // Clear cache
    this.chartDataCache = {
      line: null,
      density: null,
      table: null
    };
    
    this.cdr.detectChanges();
  }

  resetRadar(): void {
    this.resetAllData();
  }

  openInfo() {
    this.dialog.open(NutsInfoDialogComponent, { autoFocus: false, disableClose: false, width: '90%', height: '90%' })
  }

  // Legacy methods - kept for compatibility but simplified
  updateLineChart(): void {
    if (this.chartDataCache.line) {
      this.lineChartData = this.chartDataCache.line;
      this.lineChartAvailable = true;
      this.cdr.detectChanges();
    }
  }

  updateDensityPlot(): void {
    if (this.chartDataCache.density) {
      this.densityChartData = this.chartDataCache.density;
      this.lineChartAvailable = true;
      this.cdr.detectChanges();
    }
  }

  updateTableData(): void {
    if (this.chartDataCache.table) {
      this.tableData = this.chartDataCache.table;
      this.lineChartAvailable = true;
      this.cdr.detectChanges();
    }
  }

  resetCharts(): void {
    // This method is now deprecated but kept for compatibility
    // The new implementation doesn't need to reset charts unnecessarily
    console.warn('resetCharts() is deprecated. Use resetAllData() for full reset or onChartSelectionChange() for switching.');
  }

  openPredictionsDialog() {
    // Implementation as needed
  }
}